<template>
    <div>
        <!-- Show Grant Farm Access page if users are selected -->
        <GrantFarmAccess 
            v-if="showGrantAccessPage" 
            :selectedUsers="selectedUsers"
            :mode="grantAccessMode"
            @back="showGrantAccessPage = false"
            @clear-selection="clearSelection"
        />

        <!-- Show User Selection page -->
        <v-container v-else fluid>

            <!-- Breadcrumbs -->
            <div class="mb-2 mt-2">
                <v-breadcrumbs :items="breadcrumbs" class="pa-0">
                    <template v-slot:divider>
                        <v-icon>mdi-chevron-right</v-icon>
                    </template>
                </v-breadcrumbs>
            </div>

            <h1 class="font-weight-bolder my-2"> {{ $t("farm.farmsAccessControl") }}</h1>

            <!-- Data Table Card -->
            <v-card elevation="0" class="px-5 py-1 mt-2">
                
                <v-card-text>
                    <div class="d-flex align-center mt-2">
                        <div class="d-flex flex-column align-start ">
                            <h2 class="mb-2 font-weight-bold">
                                {{ $t("farm.chooseTheUserYouWantToManageFarmAccessFor") }}
                            </h2>
                            <p class="text--secondary">{{ $t("farm.chooseTheUserYouWantToManageFarmAccessFor") }}</p>
                        </div>
    
                        <v-spacer></v-spacer>
    
                        <!-- Proceed Button -->
                        <v-btn dark class="mr-2" color="primary" :disabled="selectedUsers.length === 0"
                            @click="handleProceed">
                            {{ $t("farm.proceedWithSelected") }}
                        </v-btn>
                    </div>
                    <v-row style="margin-left: -30px; margin-bottom: 20px;">
                        <v-col cols="12">
                            <v-data-table :headers="tableHeaders" :items="users" :items-per-page="options.limit"
                                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }" item-key="id"
                                hide-default-footer :loading="loading" :options.sync="options"
                                :server-items-length="totalUsers" :loading-text="$t('loadingData')" show-select
                                v-model="selectedUsers" :single-select="false">
                                <template v-slot:top>
                                    <div class="d-flex mt-5 align-center flex-wrap">
                                        <!-- Search Field -->
                                        <div class="ml-3 control" style="width: 280px;">
                                            <label for="text">&nbsp;</label>
                                            <v-text-field prepend-inner-icon="mdi-magnify" outlined placeholder="Search"
                                                dense v-model="search" @input="searchQuery"
                                                class="shrink"></v-text-field>
                                        </div>

                                        <!-- User Role Dropdown -->
                                        <div class="ml-3 control">
                                            <label>
                                                {{ $t("farm.userRole") }}
                                                <v-tooltip top color="primary" max-width="220">
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-icon v-bind="attrs" v-on="on" small class="ml-1">
                                                            mdi-information-outline
                                                        </v-icon>
                                                    </template>
                                                    <span>{{ $t("farm.userRoleInfo") }}</span>
                                                </v-tooltip>
                                            </label>
                                            <v-select v-model="userRole" :items="userRoleList" item-text="name"
                                                item-value="value" outlined dense placeholder="All"
                                                @change="handleRoleChange" style="width: 180px;" class="shrink"
                                                clearable></v-select>
                                        </div>

                                        <!-- Selected Users Indicator -->
                                        <div v-if="selectedUsers.length > 0" class="ml-3 d-flex align-center">
                                            <span class="mr-2">({{ selectedUsers.length }} {{ $t("farm.usersSelected") }})</span>
                                            <v-btn icon small @click="clearSelection">
                                                <v-icon color="red">mdi-close</v-icon>
                                            </v-btn>
                                        </div>

                                        <v-spacer></v-spacer>
                                        <!-- Pagination Info -->
                                        <div class="py-1 title">
                                            {{ currentPageItems }} {{ $t("farm.of") }} {{ totalUsers }}
                                        </div>

                                        <!-- Pagination Controls -->
                                        <v-btn class="mx-2" fab small outlined color="primary"
                                            :disabled="options.page <= 1" @click="pageChange(false)">
                                            <v-icon dark>mdi-chevron-left</v-icon>
                                        </v-btn>
                                        <v-btn class="mx-2" fab small outlined color="primary"
                                            :disabled="options.page >= totalPages" @click="pageChange(true)">
                                            <v-icon dark>mdi-chevron-right</v-icon>
                                        </v-btn>

                                        <!-- Customization Menu -->
                                        <v-menu v-model="menu" :close-on-content-click="false" :nudge-width="200" left
                                            :min-width="417">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs"
                                                    v-on="on">
                                                    <v-icon dark>mdi-cog-outline</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-card width="417">
                                                <v-list>
                                                    <v-list-item>
                                                        <v-list-item-title>{{ $t("farm.customization") }}</v-list-item-title>
                                                    </v-list-item>
                                                    <v-divider></v-divider>
                                                    <div class="pl-3 pt-2 font-weight-normal">
                                                        {{ $t("farm.recordsPerPage") }}
                                                    </div>
                                                    <v-list-item>
                                                        <v-slider v-model="itemsPerPageSlider"
                                                            :tick-labels="itemsPerPageOptions" :max="3" step="1"
                                                            ticks="always" tick-size="4"></v-slider>
                                                    </v-list-item>
                                                    <v-divider class="mt-n3"></v-divider>
                                                </v-list>
                                                <v-card-text>
                                                    <v-container fluid>
                                                        <v-row>
                                                            <v-col class="mt-n9" cols="6" v-for="header in headers"
                                                                :key="header.value">
                                                                <v-checkbox v-if="header.text" v-model="selectedHeaders"
                                                                    :label="header.text" color="primary"
                                                                    :value="header.text" hide-details></v-checkbox>
                                                            </v-col>
                                                        </v-row>
                                                    </v-container>
                                                </v-card-text>
                                                <v-layout justify-center>
                                                    <v-card-actions class="mb-5">
                                                        <v-spacer></v-spacer>
                                                        <v-btn outlined color="primary" @click="resetDefault"
                                                            width="190">
                                                            {{ $t("farm.restoreDefault") }}
                                                        </v-btn>
                                                        <v-btn color="primary" @click="resetTableStructure" width="190">
                                                            {{ $t("farm.apply") }}
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-layout>
                                            </v-card>
                                        </v-menu>

                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs"
                                                    v-on="on" @click="printPDF">
                                                    <v-icon dark>mdi-printer</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ $t("farm.print") }}</span>
                                        </v-tooltip>
                                    </div>
                                </template>

                                <!-- Individual column templates -->
                                <template v-slot:item.userNameAndId="{ item }">
                                    <div>
                                        <div class="font-weight-medium">{{ item.userName }}</div>
                                        <div class="text-caption text--secondary">{{ item.userId }}</div>
                                    </div>
                                </template>

                                <template v-slot:item.email="{ item }">
                                    <span class="text-truncate">{{ item.email || '-' }}</span>
                                </template>

                                <template v-slot:item.userRoleAndDate="{ item }">
                                    <div>
                                        <div class="font-weight-medium">{{ item.userRole }}</div>
                                        <div class="text-caption text--secondary">{{ item.registrationDate }}</div>
                                    </div>
                                </template>

                                <template v-slot:item.status="{ item }">
                                    <v-btn small rounded min-width="120" depressed :class="getStatusClass(item.status)"
                                        height="40">
                                        {{ getStatusText(item.status) }}
                                    </v-btn>
                                </template>

                                <template v-slot:item.access="{ item }">
                                    <a href="#" class="access-link" @click.prevent="selectUserAccess(item)">
                                        {{ $t("farm.selectUser") }}
                                    </a>
                                </template>

                                <template v-slot:item.actions="{ item }">
                                    <div class="d-flex justify-center">
                                        <v-menu location="start">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon v-bind="attrs" v-on="on" small>
                                                    <v-icon>mdi-dots-vertical</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list>
                                                <v-list-item @click="viewFarmAccess(item)">
                                                    <v-list-item-title>{{ $t("farm.viewFarmAccess") }}</v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </div>
                                </template>

                                <template v-slot:footer></template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-container>

        <!-- Confirmation Dialog -->
        <ConfirmationBox :dialog="confirmDeactivateDialog" actionType="reject" :message="confirmDeactivate.message"
            :confirmData="confirmDeactivate.confirmData" :dialogTitle="confirmDeactivate.title"
            :confirmButtonText="confirmDeactivate.confirmBtnText" @close="confirmDeactivateDialog = false"
            @confirm="confirmDeactivateUser" />

    </div>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import download from "@/mixins/DownloadMixin";
import html2pdf from "html2pdf.js";
import printJS from "print-js";
import ConfirmationBox from "./components/ConfirmationBox";
import GrantFarmAccess from "./components/GrantFarmAccess.vue";
import FarmService from "@/_services/FarmService";

export default {
    name: "FarmUser",
    components: {
        ConfirmationBox,
        GrantFarmAccess,
    },
    props: {
        farm: {
            type: Object,
            default: () => ({}),
        },
    },
    async mounted() {
        this.tableHeaders = this.headers;
        this.selectedHeaders = this.allSelectedHeaders;
        // Initialize slider to match current limit
        const currentLimitIndex = this.itemsPerPageOptions.indexOf(this.options.limit);
        this.itemsPerPageSlider = currentLimitIndex >= 0 ? currentLimitIndex : 1;
        this.loadAppUsers();
    },
    computed: {
        totalPages() {
            return parseInt(Math.ceil(this.totalUsers / this.options.limit));
        },
        currentPageItems() {
            if (this.totalUsers === 0) {
                return '0';
            }
            const start = (this.options.page - 1) * this.options.limit + 1;
            const end = Math.min(this.options.page * this.options.limit, this.totalUsers);
            if (start === end) {
                return start.toString();
            }
            return `${start}-${end}`;
        },
    },
    watch: {
        options: {
            handler(newVal, oldVal) {
                if (oldVal && (newVal.page !== oldVal.page || newVal.limit !== oldVal.limit)) {
                    this.loadAppUsers();
                }
            },
            deep: true,
        },
    },
    data() {
        return {
            breadcrumbs: [
                { text: this.$t("farm.dashboard"), disabled: false, href: "#" },
                { text: this.$t("farm.farms"), disabled: false, href: "#" },
                { text: this.$t("farm.accessControl"), disabled: true, href: "#" },
            ],
            selectedUsers: [],
            debounceTimer: null,
            search: "",
            userRole: "",
            userRoleList: [
                { name: this.$t("farm.all"), value: "" },
                { name: this.$t("farm.farmer"), value: "farmer" },
                { name: this.$t("farm.technician"), value: "technician" },
            ],
            loading: false,
            options: {
                limit: 10,
                page: 1,
            },
            headers: [
                {
                    text: this.$t("farm.userNameAndId"),
                    align: "start",
                    value: "userNameAndId",
                    class: "black--text",
                    sortable: true,
                },
                {
                    text: this.$t("farm.email"),
                    align: "start",
                    value: "email",
                    class: "black--text",
                    sortable: true,
                },
                {
                    text: this.$t("farm.userRoleAndRegDate"),
                    align: "start",
                    value: "userRoleAndDate",
                    class: "black--text",
                    sortable: true,
                },
                {
                    text: this.$t("farm.status"),
                    align: "start",
                    value: "status",
                    class: "black--text",
                    sortable: true,
                },
                {
                    text: this.$t("farm.access"),
                    align: "start",
                    value: "access",
                    class: "black--text",
                    sortable: false,
                },
                {
                    text: "",
                    align: "center",
                    value: "actions",
                    class: "black--text",
                    sortable: false,
                },
            ],
            allSelectedHeaders: [
                this.$t("farm.userNameAndId"),
                this.$t("farm.email"),
                this.$t("farm.userRoleAndRegDate"),
                this.$t("farm.status"),
                this.$t("farm.access"),
                "",
            ],
            menu: false,
            users: [],
            totalUsers: 0,
            itemsPerPageOptions: [5, 10, 25, 50],
            itemsPerPageSlider: 1,
            selectedHeaders: [],
            tableHeaders: [],
            showActionsColumn: true,
            hideOnPrint: false,
            confirmDeactivateDialog: false,
            confirmDeactivate: {
                title: "",
                message: "",
                confirmBtnText: "",
                cancelBtnText: this.$t("farm.cancel"),
                confirmData: {},
            },
            showGrantAccessPage: false,
            grantAccessMode: 'grant', // 'view' or 'grant'
        };
    },
    methods: {
        async loadAppUsers() {
            this.loading = true;
            try {
                const params = {
                    page: this.options.page,
                    limit: this.options.limit,
                    ...(this.search && { search: this.search }),
                    ...(this.userRole && { userType: this.userRole }),
                };
                
                const response = await FarmService.getAppUsers(params);
                
                // Handle API response structure: response.data.data.users and response.data.data.count
                if (response && response.data && response.data.data) {
                    const apiData = response.data.data;
                    
                    if (apiData.users && Array.isArray(apiData.users)) {
                        // Map API response to component format
                        this.users = apiData.users.map(user => ({
                            id: user.id,
                            userId: user.id,
                            userName: user.name || '-',
                            email: user.email || '-',
                            userRole: user.userType || '-',
                            registrationDate: user.registrationDate 
                                ? new Date(user.registrationDate).toLocaleDateString() 
                                : '-',
                            status: user.status || 'active',
                        }));
                        
                        this.totalUsers = apiData.count || 0;
                    } else {
                        this.users = [];
                        this.totalUsers = 0;
                    }
                } else {
                    this.users = [];
                    this.totalUsers = 0;
                }
            } catch (error) {
                console.error('Error loading app users:', error);
                this.$notify({
                    title: "Error",
                    message: error?.data?.message || "Failed to load users. Please try again.",
                    type: "error",
                });
                this.users = [];
                this.totalUsers = 0;
            } finally {
                this.loading = false;
            }
        },
        searchQuery() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.options.page = 1;
                this.loadAppUsers();
            }, 500);
        },
        handleRoleChange() {
            this.options.page = 1;
            this.loadAppUsers();
        },
        pageChange(next) {
            if (next) this.options.page += 1;
            else this.options.page -= 1;
            this.loadAppUsers();
        },
        async resetDefault() {
            this.tableHeaders = this.headers;
            this.selectedHeaders = this.allSelectedHeaders;
            // Reset slider to default (index 1 = 10 items per page)
            this.itemsPerPageSlider = 1;
            this.options.limit = this.itemsPerPageOptions[this.itemsPerPageSlider];
            this.options.page = 1;
            this.menu = false;
            await this.loadAppUsers();
        },
        async resetTableStructure() {
            this.tableHeaders = this.headers.filter((header) =>
                this.selectedHeaders.includes(header.text)
            );
            this.options.limit = this.itemsPerPageOptions[this.itemsPerPageSlider];
            this.options.page = 1;
            this.menu = false;
            await this.loadAppUsers();
        },
        getStatusText(status) {
            switch (status) {
                case "active":
                    return this.$t("farm.active");
                case "inactive":
                    return this.$t("farm.inactive");
                default:
                    return status || this.$t("farm.na");
            }
        },
        getStatusClass(status) {
            switch (status) {
                case "active":
                    return "green-button";
                case "inactive":
                    return "red-button";
                default:
                    return "gray-button";
            }
        },
        viewFarmAccess(item) {
            this.grantAccessMode = 'view';
            this.showGrantAccessPage = true;
            this.selectedUsers = [item];
        },
        async confirmDeactivateUser(confirmData) {
            this.confirmDeactivateDialog = false;
            const item = confirmData.item;
            // Update the status in the users array
            const userIndex = this.users.findIndex((u) => u.id === item.id);
            if (userIndex !== -1) {
                this.users[userIndex].status = item.status === "active" ? "inactive" : "active";
                this.$notify({
                    title: item.status === "active" ? this.$t("farm.userDeactivated") : this.$t("farm.userActivated"),
                    type: "success",
                });
                // Reload data to reflect changes
                await this.loadAppUsers();
            }
        },
        async printPDF() {
            this.showActionsColumn = false;
            this.hideOnPrint = true;
            await this.$nextTick();
            const table = document.querySelector(".v-data-table__wrapper table");
            let removedHeader,
                removedCells = [];
            if (table) {
                const headerRow = table.querySelector("thead tr");
                if (headerRow && headerRow.children.length > 0) {
                    removedHeader = headerRow.removeChild(headerRow.lastElementChild);
                }
                const bodyRows = table.querySelectorAll("tbody tr");
                bodyRows.forEach((row) => {
                    if (row.children.length > 0) {
                        removedCells.push(row.removeChild(row.lastElementChild));
                    }
                });
            }
            const element = document.querySelector(".v-data-table__wrapper");
            let opt = {
                margin: 1,
                filename: this.$t("farm.farmUsers") + ".pdf",
                image: { type: "jpeg", quality: 0.95 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
            };
            let worker = await html2pdf()
                .set(opt)
                .from(element)
                .toCanvas()
                .outputPdf("blob", this.$t("farm.farmUsers") + ".pdf");
            let file = new File([worker], "FarmUsers.pdf", { type: "application/pdf" });
            var pdfUrl = await URL.createObjectURL(file);
            await printJS({ printable: `${pdfUrl}`, type: "pdf", showModal: true });
            if (table && removedHeader) {
                const headerRow = table.querySelector("thead tr");
                if (headerRow) {
                    headerRow.appendChild(removedHeader);
                }
                const bodyRows = table.querySelectorAll("tbody tr");
                bodyRows.forEach((row, idx) => {
                    if (removedCells[idx]) {
                        row.appendChild(removedCells[idx]);
                    }
                });
            }
            this.showActionsColumn = true;
            this.hideOnPrint = false;
            await this.$nextTick();
        },
        clearSelection() {
            this.selectedUsers = [];
        },
        handleProceed() {
            if (this.selectedUsers.length === 0) {
                return;
            }
            // Show the Grant Farm Access page in grant mode (show all farms)
            this.grantAccessMode = 'grant';
            this.showGrantAccessPage = true;
        },
        selectUserAccess(item) {
            // Toggle selection when clicking "Select User" link
            const index = this.selectedUsers.findIndex(u => u.id === item.id);
            if (index === -1) {
                this.selectedUsers.push(item);
            } else {
                this.selectedUsers.splice(index, 1);
            }
        },
    },
    mixins: [loadingMixin, download],
};
</script>

<style lang="scss" scoped>
.green-button {
    background-color: #c1ffe7 !important;
    border: 2px solid #00bd73 !important;
    color: #489e16 !important;
}

.red-button {
    background-color: #fdecea !important;
    border: 2px solid #f03737 !important;
    color: #f03737 !important;
}

.gray-button {
    background-color: #e8e8e8 !important;
    border: 2px solid #000000 !important;
    color: #000000 !important;
}

.access-link {
    color: #1976d2 !important;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        color: #1565c0 !important;
    }
}


.control {
    min-width: 120px;
    max-width: 220px;
    flex: 1 1 160px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
}

/* Hide the action column when printing */
@media print {
    .action-col {
        display: none !important;
    }
}
</style>
